import React, { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'transfix-ui/components/Spinner'
import { TopNav } from 'transfix-ui/components/TopNav'
import { IShipmentStatus } from 'types/FMSGraphqlTypes'
import { LOAD_STATE, TAB } from 'apps/fleet-planner/constants'
import { NoLoads } from 'apps/fleet-planner/pages/loads/NoLoads'
import { ILoadFilters } from 'apps/fleet-planner/types'
import { Container, TopNavCTA } from 'components/layout'
import { LoadsTable } from './LoadsTable'
import StatusTabs from './StatusTabs'
import { LoadsFilters } from './filters/LoadsFilters'
import { useMyLoadsFilterInfoQuery } from './filters/graphql/MyLoadsFilterInfo'
import { IShipmentFragment } from './graphql/ShipmentFragment'
import { IShipmentsQueryVariables, useShipmentsQuery } from './graphql/Shipments'
import { TAB_EXTERNAL_STATUS_MAP, headerStatusCountRequest, mergeFilters } from './utils'

export const PER_PAGE = 10

export const Loads = () => {
  const defaultFiltersForRequest = {
    maxAge: 30,
    status: [IShipmentStatus.ready_for_dispatch],
    groupedStatus: headerStatusCountRequest,
    first: PER_PAGE,
  }

  const defaultFiltersForScreenForm = {
    maxAge: 30,
    status: [LOAD_STATE['Ready for Dispatch']],
    groupedStatus: headerStatusCountRequest,
    first: PER_PAGE,
  }

  const [filters, setFilters] = useState<IShipmentsQueryVariables>(defaultFiltersForRequest)
  const [loadScreenFilter, setLoadScreenFilter] = useState<Partial<ILoadFilters>>(
    defaultFiltersForScreenForm
  )
  const { data, loading } = useShipmentsQuery({ variables: filters })
  const { data: filterInfoData } = useMyLoadsFilterInfoQuery()

  const { myLoadsFilterInfo } = filterInfoData || {}
  const { customers, dispatchers, drivers, motorCarriers } = myLoadsFilterInfo || {}

  const { shipments } = data || {}
  const { groupedStatusCount, nodes, pageInfo, totalCount = 0 } = shipments || {}
  const [tabHeaderCounter, setTabHeaderCounter] = useState(groupedStatusCount)

  const [tab, setTab] = useState<string>(TAB.READY_FOR_DISPATCH)
  const { endCursor, startCursor } = pageInfo || {}

  const [pageNumber, setPageNumber] = useState<number>(1)

  useEffect(() => {
    if (groupedStatusCount) {
      setTabHeaderCounter(groupedStatusCount)
    }
  }, [groupedStatusCount])

  const onPageChange = (newPageNumber: number) => {
    setPageNumber(newPageNumber)
    if (newPageNumber < pageNumber) {
      setFilters(prevState => ({
        ...prevState,
        first: undefined,
        last: PER_PAGE,
        after: undefined,
        before: startCursor || '',
      }))
    } else if (newPageNumber > pageNumber) {
      setFilters(prevState => ({
        ...prevState,
        first: PER_PAGE,
        last: undefined,
        after: endCursor || '',
        before: undefined,
      }))
    }
  }

  const filterChangeHandler = (newFilters: Partial<ILoadFilters>) => {
    const filterObject = mergeFilters(filters, newFilters)
    setFilters(prevState => ({
      ...prevState,
      ...filterObject,
    }))

    setLoadScreenFilter(prevState => ({
      ...prevState,
      ...newFilters,
    }))
  }

  const tabChangeHandler = (newTab: string) => {
    const selectedStatus: IShipmentStatus[] = TAB_EXTERNAL_STATUS_MAP[newTab] || []
    setFilters(prevState => ({
      ...prevState,
      status: selectedStatus,
    }))
    setTab(newTab)
  }

  const clearAllFiltersHandler = useCallback(() => {
    setFilters(defaultFiltersForRequest)
    setLoadScreenFilter(defaultFiltersForScreenForm)
    setTab(TAB.READY_FOR_DISPATCH)
  }, [setFilters])

  return (
    <>
      <TopNav title='My Loads'>
        <TopNavCTA label='Add Load' onClick={() => {}} large />
      </TopNav>
      <Container hidePadding>
        <LoadsFilters
          onFilterChange={filterChangeHandler}
          currentFilters={loadScreenFilter}
          onClearFilter={clearAllFiltersHandler}
          onSortChange={() => {}}
          dispatcherList={dispatchers || []}
          driverList={drivers || []}
          customerList={customers || []}
          motorCarrierList={motorCarriers || []}
        />
        <div>
          <StatusTabs
            groupedStatusCount={tabHeaderCounter || []}
            tab={tab}
            tabChangeHandler={tabChangeHandler}>
            {loading && <Spinner position='relative' />}
            {(!loading && !nodes) ||
              (nodes?.length === 0 && (
                <NoLoads
                  onAddLoadClick={() => {}}
                  clearFiltersHandler={clearAllFiltersHandler}
                  isFilteredView
                />
              ))}
            {!loading && nodes && nodes?.length !== 0 && (
              <LoadsTable
                onPageChange={onPageChange}
                pageNumber={pageNumber}
                shipments={nodes as IShipmentFragment[]}
                totalCount={totalCount}
                currentTab={tab}
              />
            )}
          </StatusTabs>
        </div>
      </Container>
    </>
  )
}

export default Loads
